import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Calendar, TrendingUp, Download } from 'lucide-react';

interface MonthlyReport {
  year: number;
  month: number;
  monthName: string;
  income: number;
}

const Reports: React.FC = () => {
  const [reports, setReports] = useState<MonthlyReport[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Генерируем доступные годы (от 2020 до текущего года)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2020; year <= currentYear; year++) {
      years.push(year);
    }
    setAvailableYears(years);
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      // Получаем ежедневную статистику за весь год
      const dailyStats = await apiService.getDailyStatistics(365);
      
      // Группируем данные по месяцам
      const monthlyData: { [key: string]: MonthlyReport } = {};
      
      // Парсим YYYY-MM-DD как московскую дату (сервер уже считает дни по Москве)
      dailyStats.forEach((stat: any) => {
        const [y, m] = (stat.date || '').split('-').map((v: string) => parseInt(v, 10));
        if (!y || !m) return;
        const year = y;
        const month = m;
        const key = `${year}-${month}`;
        
        // Пропускаем будущие месяцы
        // Текущий месяц по Москве (UTC+3)
        const now = new Date(Date.now() + 3 * 60 * 60 * 1000);
        const currYear = now.getUTCFullYear();
        const currMonth = now.getUTCMonth() + 1;
        if (year > currYear || (year === currYear && month > currMonth)) {
          return;
        }
        
        if (!monthlyData[key]) {
          const monthNames = [
            'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
            'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
          ];
          
          monthlyData[key] = {
            year,
            month,
            monthName: monthNames[month - 1],
            income: 0
          };
        }
        
        monthlyData[key].income += stat.daily_income || 0;
      });
      
      // Фильтруем по выбранному году
      const filteredReports = Object.values(monthlyData)
        .filter(report => report.year === selectedYear)
        .sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.month - a.month;
        });
      
      setReports(filteredReports);
    } catch (error) {
      console.error('Ошибка при загрузке отчетов:', error);
      toast.error('Ошибка при загрузке отчетов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [selectedYear]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatCurrencyForExport = (amount: number) => {
    // Форматируем число для Excel: используем точку как десятичный знак
    return amount.toFixed(2);
  };

  const getTotalIncome = () => reports.reduce((sum, report) => sum + report.income, 0);

  const exportToExcel = () => {
    if (reports.length === 0) {
      toast.error('Нет данных для экспорта');
      return;
    }

    try {
      // Создаем заголовки CSV
      const headers = [
        'Месяц',
        'Год',
        'Доход'
      ];

      // Создаем строки данных
      const dataRows = reports.map(report => [
        report.monthName,
        report.year.toString(),
        formatCurrencyForExport(report.income)
      ]);

      // Добавляем итоговую строку
      const totalRow = [
        'ИТОГО',
        selectedYear.toString(),
        formatCurrencyForExport(getTotalIncome())
      ];

      // Объединяем все строки, разделяя столбцы точкой с запятой
      const csvContent = [
        headers.join(';'),
        ...dataRows.map(row => row.join(';')),
        totalRow.join(';')
      ].join('\n');

      // Создаем BOM для корректного отображения кириллицы в Excel
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Создаем ссылку для скачивания
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `отчет_${selectedYear}_год.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Отчет успешно выгружен');
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      toast.error('Ошибка при экспорте отчета');
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Отчеты</h1>
            <p className="text-gray-400">Месячная статистика доходов</p>
          </div>
          
          {/* Кнопка экспорта */}
          <button
            onClick={exportToExcel}
            disabled={reports.length === 0 || loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Download size={16} />
            Экспорт в Excel
          </button>
        </div>
      </div>

      {/* Выбор года */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <label className="text-white font-medium">Выберите год:</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedYear(prev => Math.max(2020, prev - 1))}
              disabled={selectedYear <= 2020}
              className="p-2 rounded-lg bg-[#23262F] text-white hover:bg-[#353945] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-4 py-2 bg-[#23262F] border border-[#353945] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            
            <button
              onClick={() => setSelectedYear(prev => Math.min(new Date().getFullYear(), prev + 1))}
              disabled={selectedYear >= new Date().getFullYear()}
              className="p-2 rounded-lg bg-[#23262F] text-white hover:bg-[#353945] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Итоговая статистика */}
      <div className="mb-8">
        <div className="bg-[#23262F] rounded-lg p-6 border border-[#353945]">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="text-green-400" size={20} />
            <h3 className="text-white font-medium">Общий доход за {selectedYear} год</h3>
          </div>
          <p className="text-2xl font-bold text-green-400">{formatCurrency(getTotalIncome())}</p>
        </div>
      </div>

      {/* Таблица отчетов */}
      <div className="bg-[#23262F] rounded-lg border border-[#353945] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#353945]">
          <h2 className="text-xl font-semibold text-white">Месячные отчеты за {selectedYear} год</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-2">Загрузка отчетов...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-400">Нет данных за выбранный период</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1A1D23]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    Месяц
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-300 uppercase tracking-wider">
                    Доход
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#353945]">
                {reports.map(report => (
                  <tr key={`${report.year}-${report.month}`} className="hover:bg-[#1A1D23] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-white font-medium">{report.monthName}</span>
                        <span className="text-gray-400 ml-2">{report.year}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-green-400 font-medium">{formatCurrency(report.income)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports; 